import {
  apiAuthenticatedRequest,
  ApiClientError,
  isApiConfigured,
} from '@/services/api/apiClient';
import type {
  WorkerAvailabilityResult,
  WorkerReservationRecord,
} from '@/types/api';
import { appConfig } from '@/config/app';
import type { AvailabilityCheckParams } from '@/modules/reservations/services/availabilityService';
import type {
  Reservation,
  ReservationInput,
} from '@/modules/reservations/types';

export function isWorkerReservationsEnabled(): boolean {
  return isApiConfigured();
}

export async function checkReservationAvailability(
  params: AvailabilityCheckParams,
): Promise<WorkerAvailabilityResult> {
  return apiAuthenticatedRequest<WorkerAvailabilityResult>(
    '/reservations/check-availability',
    {
      method: 'POST',
      body: {
        inventoryItemIds: params.inventoryItemIds,
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.excludeReservationId
          ? { excludeReservationId: params.excludeReservationId }
          : {}),
      },
    },
  );
}

export async function createReservationViaWorker(
  input: ReservationInput,
): Promise<Reservation> {
  const record = await apiAuthenticatedRequest<WorkerReservationRecord>(
    '/reservations/create',
    {
      method: 'POST',
      body: input,
    },
  );
  return mapWorkerReservation(record);
}

export async function updateReservationViaWorker(
  id: string,
  input: ReservationInput,
  expectedUpdatedAt?: string,
): Promise<Reservation> {
  const record = await apiAuthenticatedRequest<WorkerReservationRecord>(
    `/reservations/${id}`,
    {
      method: 'PATCH',
      body: {
        ...input,
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
      },
    },
  );
  return mapWorkerReservation(record);
}

export async function cancelReservationViaWorker(id: string): Promise<Reservation> {
  const record = await apiAuthenticatedRequest<WorkerReservationRecord>(
    `/reservations/${id}/cancel`,
    { method: 'POST' },
  );
  return mapWorkerReservation(record);
}

export function mapWorkerConflictsToReservations(
  conflicts: WorkerAvailabilityResult['conflicts'],
): Reservation[] {
  return conflicts.map((c) => ({
    id: c.id,
    companyId: appConfig.companyId,
    title: c.title,
    description: '',
    inventoryItemIds: c.inventoryItemIds,
    clientName: c.clientName,
    startDate: c.startDate,
    endDate: c.endDate,
    status: c.status,
    notes: '',
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  }));
}

function mapWorkerReservation(record: WorkerReservationRecord): Reservation {
  return {
    id: record.id,
    companyId: record.companyId,
    title: record.title,
    description: record.description,
    inventoryItemIds: record.inventoryItemIds,
    clientName: record.clientName,
    startDate: record.startDate,
    endDate: record.endDate,
    status: record.status,
    notes: record.notes,
    createdBy: record.createdBy,
    createdAt: new Date(record.createdAt),
    updatedAt: new Date(record.updatedAt),
  };
}

function isAvailabilityConflictDetails(
  value: unknown,
): value is WorkerAvailabilityResult {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return o.available === false && Array.isArray(o.conflicts);
}

export function formatReservationApiError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 409) {
      if (isAvailabilityConflictDetails(err.details)) {
        const n =
          err.details.conflictDetails?.length ??
          err.details.conflicts.length;
        if (n > 0) {
          return `Conflicto de disponibilidad con ${n} reserva(s) en el rango seleccionado.`;
        }
        if (err.details.ineligibleItems.length > 0) {
          return 'Uno o más artículos no están disponibles para reservar.';
        }
      }
      const detailCode =
        err.details &&
        typeof err.details === 'object' &&
        'code' in err.details &&
        (err.details as { code: string }).code === 'concurrent_modification';
      if (detailCode) {
        return err.message;
      }
      return err.workerError ?? err.message;
    }
    if (err.status === 401) {
      return 'Sesión expirada. Volvé a iniciar sesión.';
    }
    if (err.status === 403) {
      return err.message;
    }
    return err.workerError ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Error al comunicarse con el Worker.';
}
